<template>
	<div id="thread-list" @scroll="getNextThreads" ref="threadList" v-if="threads.length > 0 || newParticipant">
		<div v-if="newParticipant">
			<thread-item :newParticipant="[newParticipant]" :threadId="null"></thread-item>
		</div>
		<thread-item
			v-for="thread in threads"
			:key="thread.id"
			:threadId="thread.id"
			:activeThread="activeThread"
			v-on="$listeners">
		</thread-item>
		<div v-if="showLoader" class="tw-py-4 tw-px-4 tw-bg-white tw-font-3xl tw-text-center">
			<i class="fa fa-spinner fa-spin tw-text-2xl tw-text-gray-300"></i>
		</div>
	</div>
	<div v-else-if="zeroThreads">
		<div class="tw-flex tw-flex-col tw-items-center tw-pt-4">
			<list-icon :color="settings.top_bar_background" />
			<div class="tw-my-6 tw-font-semibold">{{ translations['messages.no_chat'] }}</div>
		</div>
	</div>
	<div v-else>
		<div class="search-skeleton-wrapper tw-relative">
			<div v-for="index in 10" :key="index" class="thread-skeleton-item tw-flex tw-items-center tw-p-5 tw-m-5">
				<div class="avatar tw-w-16 tw-h-16 tw-rounded-full tw-mr-4 tw-bg-gray-200"></div>

				<div>
					<div class="tw-h-4 tw-w-40 tw-mb-2 tw-bg-gray-200 tw-rounded" ></div>
					<div class="tw-h-3 tw-w-24 tw-mb-2 tw-bg-gray-200 tw-rounded"></div>
					<div class="tw-h-2 tw-w-14 tw-bg-gray-200 tw-rounded"></div>
				</div>
			</div>

            <div class="skeleton-animation"></div>
		</div>
	</div>
</template>

<script>
import ThreadItem from './ThreadItem';
import ListIcon from './icons/ListIcon';
import Thread from '../../lib/models/Thread';
import Global from '../../mixins/Global.js';

export default {
	name: 'ThreadList',
	props: ['activeThread', 'newParticipantId'],
	data() {
		return {
			newParticipant: null,
			showLoader: false,
			curPage: 1,
			lastPage: null,
			threadsFetched: false,
			zeroThreads: false
		};
	},
	mixins: [Global],
	components: {
		'thread-item': ThreadItem,
		'list-icon': ListIcon,
	},
	watch: {
		newParticipantId: function (oldValue, newValue) {
			if (oldValue !== newValue) {
				this.getParticipant(this.newParticipantId);
			}
		},
	},
	computed: {
		threads() {
			return Thread.query().orderBy('last_activity', 'desc').get();
		},
	},
	methods: {
		listenForIncoming() {
			window.Echo.private(`messages.${self.user.id}`)
				.listen('.inbox', async (message) => {

					const _message = {...message.message};

					//update thread list info with new message
					const threadId = _message.fake_thread_id || _message.thread_id;
					let foundThread = await Thread.find(threadId);

					if(foundThread) {
						// there is a existing thread, update the list with new message
						const last_activity_readable = this.timeDifference(_message.created_at);
						Thread.update({
							where: threadId,
							data(thread) {
								thread.last_activity_readable = last_activity_readable;
								thread.last_activity = _message.created_at;
								thread.messages[0] = _message;
							}
						});
					} else {
						// there is a new thread created, so store this in foundThread
						const response = await Thread.api().fetchById(threadId);
						foundThread = response.response.data.data;
					}

					if(_message.user_id !== self.user.id) {
						// if the incoming message is from other user, insert the message as is to display it as a new incoming message
						Message.insert({
							data: [
								_message
							],
						});

						// when the user is in the active chat, we can update the last_read of that user
						if(this.thread_id === _message.thread_id) {
							Thread.api().updateLastActivity(_message.thread_id, self.user.id);
						}
					} else {

						// eslint-disable-next-line no-prototype-builtins
						if(foundThread.hasOwnProperty('sent_message_ids')) {
							// there is a 'fake' message that we need to update
							_message.new = true;
							_message.not_send = false;

							let sentMessageIds = [...foundThread.sent_message_ids];
							let foundMessages =  [];

							// get the existing messages that we need to update
							sentMessageIds.forEach(async (id) => {
								foundMessages.push(Message.find(id));
							});

							foundMessages.forEach(async (messageItem) => {
								Message.update({
									where: messageItem.id,
									data(message) {
										message.new = false;
										message.not_send = false;
										message.attachments = _message.attachments.length > 0 ? _message.attachments : [];
									}
								});

								// remove the updated message id from the thread message array
								let index = sentMessageIds.indexOf(messageItem.id);
								if (index > -1) {
									sentMessageIds.splice(index, 1);
								}

								Thread.update({
									where: threadId,
									data(thread) {
										thread.sent_message_ids = sentMessageIds; // new array
									}
								});
							});
						}

					}
				});
		},

		fetch(pageId) {
			this.threadsFetched = false;

			Thread.api()
				.fetch(pageId)
				.then((response) => {
					if(response.response.data.data.length === 0) {
						// there are no chats started
						this.zeroThreads = true;
						this.$emit('set-empty-state', true);
					}

					this.threadsFetched = true;
					this.lastPage = response.response.data.meta.pagination.last_page;
					this.showLoader = false;

					// check if the searched conversation is in the thread list
					// if not, it is deleted earlier so we need to fetch it again..
					// activeThread can be null if we land on /messages
					if (this.activeThread !== null && this.threads.some(e => { return e.id !== this.activeThread; })) {
						Thread.api().fetchById(this.activeThread);
					}

					this.$emit('set-initial-thread');
				});
		},
		getParticipant(id) {
			if (this.newParticipantId) {
				const url = `/api/v3/users/${id}`;
				fetch(url)
					.then((response) => response.json())
					.then((data) => {
						this.newParticipant = data.data;
					});
			} else {
				this.newParticipant = null;
			}
		},
		getNextThreads() {
			if (this.curPage === this.lastPage) return;

			if (this.threadsFetched === true) {
				const div = this.$refs.threadList;

				// *0.99 to load just before user reaches the end of the list
				const bottomOfWindow =
					div.scrollTop + div.clientHeight >= div.scrollHeight * 0.99;

				if (bottomOfWindow) {
					this.showLoader = true;
					this.curPage += 1;
					this.fetch(this.curPage);
				}
			}
		},
	},
	mounted() {
		this.fetch(this.curPage);
		
		this.listenForIncoming(); 

		if (this.newParticipantId) {
			this.getParticipant(this.newParticipantId);
		}
	},
};
</script>

<style lang="scss" scoped>
	.search-skeleton-wrapper {
		.skeleton-animation {
            @apply tw-top-0 tw-absolute tw-opacity-50 tw-bg-white;
			height: 110%;
			width: 50px;
			transform: rotate(15deg);
			-webkit-animation: loading-topic-skeleton 1.5s ease infinite;
			animation: loading-topic-skeleton 1.5s ease infinite;
		}

        .thread-skeleton-item {
            &:first-of-type {
                margin-top: 0;
            }
        }
	}

	@-webkit-keyframes loading-topic-skeleton {
		0% {
			left: -110%;
		}
		50% {
			left: 110%;
		}
		100% {
			left: 110%;
		}
	}

	@keyframes loading-topic-skeleton {
		0% {
			left: -110%;
		}
		50% {
			left: 110%;
		}
		100% {
			left: 110%;
		}
	}

</style>
